import React, { useState, useRef, useEffect } from 'react';
import { Sun, Moon, Menu, X, ChevronDown, Shield } from 'lucide-react';
import logo from '/assets/logo.png';

const solutions = [
  { text: 'Pulse Security', page: 'pulse-security' },
  { text: 'Neutron Security', page: 'neutron-security' },
  { text: 'Quantum Security', page: 'quantum-security' }
];

const industries = [
  { text: 'Financial Services', page: 'financial-services' },
  { text: 'Healthcare', page: 'healthcare' },
  { text: 'Government', page: 'government' },
  { text: 'Manufacturing', page: 'manufacturing' },
  { text: 'Retail', page: 'retail' },
  { text: 'Technology', page: 'technology' },
  { text: 'Education', page: 'education' }
];

const services = [
  { text: 'Managed Security', page: 'managed-security' },
  { text: 'Security Operations Center', page: 'soc' },
  { text: 'Cloud Security', page: 'cloud-security' },
  { text: 'Compliance & Risk', page: 'compliance' },
  { text: 'Transformation', page: 'transformation' },
  { text: 'Specialized Industry', page: 'specialized-industry' },
  { text: 'Threat Intelligence', page: 'threat-intelligence' },
  { text: 'Incident Response', page: 'incident-response' },
  { text: 'Data Security', page: 'data-security' },
  { text: 'Training & Awareness', page: 'training' }
];

const consulting = [
  { text: 'Managed Services', page: 'managed-services' },
  { text: 'Professional Services', page: 'professional-services' },
  { text: 'Global Delivery', page: 'global-delivery' }
];

const company = [
  { text: 'About Us', page: 'about' },
  { text: 'Contact', page: 'contact' },
  { text: 'Why Choose AstronShield', page: 'why-choose-us' },
  { text: '911-AstronShield', page: '911-astronshield' },
  { text: 'Careers', page: 'careers' },
  { text: 'Leadership', page: 'leadership' },
  { text: 'Privacy Notice', page: 'privacy' },
  { text: 'Locations', page: 'locations' }
];

interface NavigationProps {
  darkMode: boolean;
  setDarkMode: (value: boolean) => void;
  isMenuOpen: boolean;
  setIsMenuOpen: (value: boolean) => void;
  setCurrentPage: (page: string) => void;
}

const Navigation: React.FC<NavigationProps> = ({
  darkMode,
  setDarkMode,
  isMenuOpen,
  setIsMenuOpen,
  setCurrentPage
}) => {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const dropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleHomeClick = () => {
    setCurrentPage('home');
  };

  const handleMouseEnter = (menu: string) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setActiveDropdown(menu);
  };

  const handleMouseLeave = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    dropdownTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 150);
  };

  useEffect(() => {
    return () => {
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
      }
    };
  }, []);

  return (
    <nav className="fixed w-full z-50 nav-blur">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <button 
              onClick={handleHomeClick}
              className="flex items-center space-x-3 text-gray-900 dark:text-white text-xl font-bold"
            >
              <img src={logo} alt="AstronShield" className="h-12 w-auto" />
              <span>AstronShield</span>
            </button>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {/* Home Tab */}
            <button 
              onClick={handleHomeClick}
              className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
            >
              Home
            </button>

            {/* Solutions Dropdown */}
            <div 
              className="relative group"
              onMouseEnter={() => handleMouseEnter('solutions')}
              onMouseLeave={handleMouseLeave}
            >
              <button 
                className="flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white py-2"
              >
                Solutions
                <ChevronDown className="ml-1 h-4 w-4" />
              </button>
              {activeDropdown === 'solutions' && (
                <div className="absolute left-0 mt-1 w-56 rounded-xl shadow-lg glass-panel">
                  <div className="py-2">
                    {solutions.map((item) => (
                      <button
                        key={item.page}
                        onClick={() => setCurrentPage(item.page)}
                        className="block w-full text-left px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-white/5"
                      >
                        {item.text}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Services Dropdown */}
            <div 
              className="relative group"
              onMouseEnter={() => handleMouseEnter('services')}
              onMouseLeave={handleMouseLeave}
            >
              <button 
                className="flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white py-2"
              >
                Services
                <ChevronDown className="ml-1 h-4 w-4" />
              </button>
              {activeDropdown === 'services' && (
                <div className="absolute left-0 mt-1 w-64 rounded-xl shadow-lg glass-panel">
                  <div className="py-2">
                    {services.map((item) => (
                      <button
                        key={item.page}
                        onClick={() => setCurrentPage(item.page)}
                        className="block w-full text-left px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-white/5"
                      >
                        {item.text}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Consulting Dropdown */}
            <div 
              className="relative group"
              onMouseEnter={() => handleMouseEnter('consulting')}
              onMouseLeave={handleMouseLeave}
            >
              <button 
                className="flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white py-2"
              >
                Consulting
                <ChevronDown className="ml-1 h-4 w-4" />
              </button>
              {activeDropdown === 'consulting' && (
                <div className="absolute left-0 mt-1 w-56 rounded-xl shadow-lg glass-panel">
                  <div className="py-2">
                    {consulting.map((item) => (
                      <button
                        key={item.page}
                        onClick={() => setCurrentPage(item.page)}
                        className="block w-full text-left px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-white/5"
                      >
                        {item.text}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Industries Dropdown */}
            <div 
              className="relative group"
              onMouseEnter={() => handleMouseEnter('industries')}
              onMouseLeave={handleMouseLeave}
            >
              <button 
                className="flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white py-2"
              >
                Industries
                <ChevronDown className="ml-1 h-4 w-4" />
              </button>
              {activeDropdown === 'industries' && (
                <div className="absolute left-0 mt-1 w-56 rounded-xl shadow-lg glass-panel">
                  <div className="py-2">
                    {industries.map((item) => (
                      <button
                        key={item.page}
                        onClick={() => setCurrentPage(item.page)}
                        className="block w-full text-left px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-white/5"
                      >
                        {item.text}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Company Dropdown */}
            <div 
              className="relative group"
              onMouseEnter={() => handleMouseEnter('company')}
              onMouseLeave={handleMouseLeave}
            >
              <button 
                className="flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white py-2"
              >
                Company
                <ChevronDown className="ml-1 h-4 w-4" />
              </button>
              {activeDropdown === 'company' && (
                <div className="absolute left-0 mt-1 w-56 rounded-xl shadow-lg glass-panel">
                  <div className="py-2">
                    {company.map((item) => (
                      <button
                        key={item.page}
                        onClick={() => setCurrentPage(item.page)}
                        className="block w-full text-left px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-white/5"
                      >
                        {item.text}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => setCurrentPage('get-started')}
              className="px-4 py-2 bg-gradient-to-r from-primary to-accent text-white rounded-full font-semibold hover:opacity-90 transition-opacity"
            >
              Request Consultation
            </button>

            <button
              onClick={() => setDarkMode(!darkMode)}
              className="p-2 rounded-lg bg-white/5 text-gray-600 dark:text-gray-300"
            >
              {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setDarkMode(!darkMode)}
              className="p-2 rounded-lg bg-white/5 text-gray-600 dark:text-gray-300 mr-2"
            >
              {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-lg bg-white/5 text-gray-600 dark:text-gray-300"
            >
              {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 glass-panel">
            {/* Home Button for Mobile */}
            <button 
              onClick={() => {
                handleHomeClick();
                setIsMenuOpen(false);
              }}
              className="block w-full text-left px-3 py-2 text-gray-600 dark:text-gray-300 hover:bg-white/5 border-b border-white/10"
            >
              Home
            </button>

            {/* Mobile Menu Sections */}
            {[
              { title: 'Solutions', items: solutions },
              { title: 'Services', items: services },
              { title: 'Consulting', items: consulting },
              { title: 'Industries', items: industries },
              { title: 'Company', items: company }
            ].map((section, index) => (
              <div key={index} className="border-b border-white/10">
                <button
                  onClick={() => {
                    const current = activeDropdown;
                    setActiveDropdown(current === section.title ? null : section.title);
                  }}
                  className="flex items-center justify-between w-full px-3 py-2 text-gray-600 dark:text-gray-300"
                >
                  <span className="font-semibold">{section.title}</span>
                  <ChevronDown 
                    className={`h-5 w-5 transition-transform ${
                      activeDropdown === section.title ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {activeDropdown === section.title && (
                  <div className="py-2 bg-white/5">
                    {section.items.map((item) => (
                      <button
                        key={item.page}
                        onClick={() => {
                          setCurrentPage(item.page);
                          setIsMenuOpen(false);
                          setActiveDropdown(null);
                        }}
                        className="block w-full text-left px-6 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-white/10"
                      >
                        {item.text}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            <button 
              onClick={() => {
                setCurrentPage('get-started');
                setIsMenuOpen(false);
              }}
              className="block w-full text-left px-3 py-2 mt-4 bg-gradient-to-r from-primary to-accent text-white rounded-lg font-semibold"
            >
              Request Consultation
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;